import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/shared/auth/useAuth'
import { formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { ThermalLogo } from '@/shared/ui/ThermalLogo'
import type { Receipt, ReceiptItem, ReceiptSale } from './cashdeskApi'
import { paymentCategoryName } from './paymentCategoryApi'
import {
  RECEIPT_CONTENT_MM,
  RECEIPT_FONT,
  RECEIPT_LOGO_MAX_HEIGHT_MM,
  RECEIPT_PRINT_ROOT,
  RECEIPT_STYLE_ID,
  RECEIPT_WIDTH_MM,
  receiptPrintCss,
} from './receiptPrint'

interface ReceiptSlipProps {
  receipt: Receipt
  copy: boolean
}

const rule: CSSProperties = { borderTop: '0.3mm dashed #000', margin: '2mm 0' }
const small: CSSProperties = { fontSize: '2.8mm' }

function Line({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '2mm' }}>
      <span>{label}</span>
      <span style={{ textAlign: 'end', overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  )
}

export function ReceiptSlip({ receipt, copy }: ReceiptSlipProps) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const [logoBroken, setLogoBroken] = useState(false)
  const company = receipt.company
  const companyName = company.name ?? user?.tenant.name ?? null
  const contacts = [company.address, company.phone, company.email, company.website].filter(
    (value): value is string => Boolean(value),
  )
  const reversal = receipt.kind === 'REVERSAL'

  const itemLabel = (item: ReceiptItem): string => {
    if (item.kind === 'TOPUP' && item.category) {
      const name = paymentCategoryName({ code: item.category, name: item.name ?? item.category }, t)
      return t('cashdesk.item.topupCategory', { name })
    }
    if (item.kind === 'TOPUP') return t('cashdesk.item.TOPUP')
    if (item.kind === 'PACKAGE') {
      return t('cashdesk.item.packageFromBalance', { name: item.name ?? t('common.dash') })
    }
    return item.name ?? t('cashdesk.item.SERVICE')
  }

  return (
    <div
      dir={i18n.dir()}
      style={{
        width: `${RECEIPT_CONTENT_MM}mm`,
        padding: `3mm ${(RECEIPT_WIDTH_MM - RECEIPT_CONTENT_MM) / 2}mm`,
        fontFamily: RECEIPT_FONT,
        fontSize: '3.2mm',
        lineHeight: 1.35,
        color: '#000',
        background: '#fff',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        {company.logoUrl && !logoBroken ? (
          <ThermalLogo
            src={company.logoUrl}
            onError={() => setLogoBroken(true)}
            style={{
              display: 'block',
              maxHeight: `${RECEIPT_LOGO_MAX_HEIGHT_MM}mm`,
              maxWidth: '56mm',
              margin: '0 auto 1.5mm',
            }}
          />
        ) : null}
        {companyName ? <div style={{ fontWeight: 700, fontSize: '3.8mm' }}>{companyName}</div> : null}
        {contacts.map((value) => (
          <div key={value} style={small}>
            <bdi>{value}</bdi>
          </div>
        ))}
      </div>

      <div style={rule} />
      <div style={{ textAlign: 'center', fontWeight: 700 }}>
        {reversal ? t('cashdesk.receipt.titleReversal') : t('cashdesk.receipt.title')}
      </div>
      {copy ? (
        <div
          style={{
            textAlign: 'center',
            fontWeight: 700,
            border: '0.4mm solid #000',
            margin: '1mm 8mm',
            letterSpacing: '0.5mm',
          }}
        >
          {t('cashdesk.receipt.copyMark')}
        </div>
      ) : null}

      <Line label={t('cashdesk.receipt.number')} value={<bdi>{receipt.receiptNo}</bdi>} />
      {reversal && receipt.reversesReceiptNo ? (
        <Line label={t('cashdesk.receipt.original')} value={<bdi>{receipt.reversesReceiptNo}</bdi>} />
      ) : null}
      <Line label={t('cashdesk.receipt.date')} value={<bdi>{formatDateTime(receipt.createdAt)}</bdi>} />
      <Line
        label={t('cashdesk.receipt.branch')}
        value={
          <>
            {receipt.branch.name} (<bdi>{receipt.branch.code}</bdi>)
          </>
        }
      />
      <Line label={t('cashdesk.receipt.cashier')} value={receipt.cashier.name} />

      <div style={rule} />
      {receipt.customer.customerId ? (
        <Line label={t('cashdesk.receipt.customerId')} value={<bdi>{receipt.customer.customerId}</bdi>} />
      ) : null}
      <Line label={t('cashdesk.receipt.login')} value={<bdi>{receipt.customer.login}</bdi>} />
      {receipt.customer.fullName ? (
        <Line label={t('cashdesk.receipt.fullName')} value={receipt.customer.fullName} />
      ) : null}
      {receipt.customer.phone ? (
        <Line label={t('cashdesk.receipt.phone')} value={<bdi>{receipt.customer.phone}</bdi>} />
      ) : null}

      <div style={rule} />
      {receipt.items.map((item, index) => (
        <Line
          key={`${item.kind}-${index}`}
          label={itemLabel(item)}
          value={<bdi>{formatMoneyValue(item.amount)}</bdi>}
        />
      ))}

      {receipt.sale ? <SaleBlock sale={receipt.sale} /> : null}

      <div style={rule} />
      <div style={{ fontWeight: 700, fontSize: '3.8mm' }}>
        <Line label={t('cashdesk.receipt.total')} value={<bdi>{formatMoneyValue(receipt.amount)}</bdi>} />
      </div>
      <Line
        label={t('cashdesk.receipt.method')}
        value={t(`cashdesk.method.${receipt.method}`, { defaultValue: receipt.method })}
      />
      <Line label={t('cashdesk.receipt.txn')} value={<bdi>#{receipt.ledgerTxnId}</bdi>} />
      {receipt.invoice?.invoiceNo ? (
        <Line label={t('sales.slip.invoice')} value={<bdi>{receipt.invoice.invoiceNo}</bdi>} />
      ) : null}

      {reversal && receipt.reason ? (
        <div style={{ marginTop: '1mm' }}>
          {t('cashdesk.receipt.reason')}: {receipt.reason}
        </div>
      ) : null}
      {receipt.status === 'PARTIAL' ? (
        <div style={{ ...small, marginTop: '1mm' }}>{t('cashdesk.receipt.partialNote')}</div>
      ) : null}

      <div style={rule} />
      <div style={{ ...small, textAlign: 'center' }}>{t('cashdesk.receipt.thanks')}</div>
    </div>
  )
}

function SaleBlock({ sale }: { sale: ReceiptSale }) {
  const { t } = useTranslation()
  const discountLabel =
    sale.discountDecision === 'APPROVAL' && sale.discountApprovedBy
      ? t('sales.slip.discountApproved', { name: sale.discountApprovedBy })
      : t('sales.slip.discount')
  return (
    <>
      <div style={rule} />
      <Line
        label={t('sales.slip.price', { name: sale.planName ?? t('common.dash') })}
        value={<bdi>{formatMoneyValue(sale.price)}</bdi>}
      />
      {sale.discount && sale.discount.amount > 0 ? (
        <Line label={discountLabel} value={<bdi>-{formatMoneyValue(sale.discount)}</bdi>} />
      ) : null}
      {sale.discount && sale.discount.amount > 0 && sale.discountReason ? (
        <div style={small}>{sale.discountReason}</div>
      ) : null}
      <Line label={t('sales.slip.paid')} value={<bdi>{formatMoneyValue(sale.paid)}</bdi>} />
      <Line label={t('sales.slip.balanceBefore')} value={<bdi>{formatMoneyValue(sale.balanceBefore)}</bdi>} />
      <Line label={t('sales.slip.balanceAfter')} value={<bdi>{formatMoneyValue(sale.balanceAfter)}</bdi>} />
      {sale.outstanding.amount > 0 ? (
        <div style={{ fontWeight: 700 }}>
          <Line label={t('sales.slip.outstanding')} value={<bdi>{formatMoneyValue(sale.outstanding)}</bdi>} />
        </div>
      ) : null}
      {sale.creditLimitAfter ? (
        <Line
          label={t('sales.slip.creditLimit')}
          value={
            <bdi>
              {formatMoneyValue(sale.creditLimitBefore)} → {formatMoneyValue(sale.creditLimitAfter)}
            </bdi>
          }
        />
      ) : null}
    </>
  )
}

/** Chop etishda faqat chek qoladi; qoidalar komponent bilan birga olib tashlanadi. */
export function ReceiptPrintPortal({ receipt, copy }: ReceiptSlipProps) {
  useEffect(() => {
    const style = document.createElement('style')
    style.id = RECEIPT_STYLE_ID
    style.textContent = receiptPrintCss()
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  return createPortal(
    <div className={RECEIPT_PRINT_ROOT} aria-hidden="true">
      <ReceiptSlip receipt={receipt} copy={copy} />
    </div>,
    document.body,
  )
}
